import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { BaseCommand, ICommand } from '../../commands/ICommand';
import { ToolComponentFactory } from '../components/ToolComponents';
import { Link, Heart, Users, Baby } from 'lucide-react';
import { getNodeMutationManager } from '../../commands/mutations/NodeMutationManager';

// Generic command for creating connectors
export class CreateConnectorCommand extends BaseCommand {
  protected connector: any;
  
  constructor(
    connector: any,
    _addConnector?: (connector: any) => void, // DEPRECATED: Use MutationManager
    _deleteConnector?: (id: string) => void, // DEPRECATED: Use MutationManager
    description?: string
  ) {
    super('Create Connector', description || `Create connector`);
    this.connector = connector;
    // Note: No longer storing callbacks - use MutationManager instead
  }
  
  execute(): void {
    // Use MutationManager to write to Yjs (single source of truth)
    const mutationManager = getNodeMutationManager();
    mutationManager.addEdge(this.connector);
    
    console.log('[CreateConnectorCommand] ✅ Added connector via MutationManager:', {
      id: this.connector.id,
      type: this.connector.type
    });
  }
  
  undo(): void {
    // Use MutationManager to delete from Yjs (single source of truth)
    const mutationManager = getNodeMutationManager();
    mutationManager.deleteEdge(this.connector.id);
    
    console.log('[CreateConnectorCommand] ✅ Removed connector via MutationManager:', this.connector.id);
  }
}

// Base connector tool
export abstract class ConnectorTool extends BaseTool {
  protected isActive = false;
  protected firstNodeId: string | null = null;
  public connectionType: string = 'default';
  public setConnectionState: ((isConnecting: boolean, firstNode: string | null) => void) | null = null;
  
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Connect',
      icon: '🔗',
      category: 'create',
      description: 'Create connections',
      version: '1.0.0',
      
      requiresSelection: false,
      
      ui: {
        toolbar: true,
        contextMenu: true,
        propertyPanel: false
      },
      
      capabilities: {
        modifiesShapes: false,
        createsShapes: false,
        deletesShapes: false,
        modifiesConnectors: false,
        createsConnectors: true,
        deletesConnectors: false,
        requiresUserInput: true,
        supportsMultiSelect: false,
        supportsBatchOperations: false
      },
      
      shortcuts: [{
        key: 'c',
        modifiers: [],
        description: 'Connect items'
      }]
    };
  }
  
  onActivate(_context: IToolContext): void {
    super.onActivate(_context);
    this.isActive = true;
    this.firstNodeId = null;
    // Change cursor to crosshair when active
    if (_context.stage) {
      _context.stage.container().style.cursor = 'crosshair';
    }
  }
  
  onDeactivate(): void {
    this.isActive = false;
    this.firstNodeId = null;
    // Reset cursor
    if (this.context?.stage) {
      this.context.stage.container().style.cursor = 'default';
    }
  }
  
  canExecute(_context: IToolContext): boolean {
    return true;
  }
  
  // Abstract methods that must be implemented
  abstract getConnectionTypes(): { value: string; label: string; icon?: string }[];
  abstract createConnector(from: string, to: string, type: string): any;
  abstract connectorExists(from: string, to: string): boolean;
  abstract addConnector(connector: any): void;
  abstract deleteConnector(id: string): void;
  
  execute(params: { from?: string; to?: string; type?: string }): ICommand | null {
    if (params.type) {
      this.connectionType = params.type;
    }
    
    if (params.from && params.to) {
      // Direct connector creation
      if (this.connectorExists(params.from, params.to)) {
        return null;
      }
      
      const connector = this.createConnector(params.from, params.to, this.connectionType);
      
      return new CreateConnectorCommand(
        connector,
        (c) => this.addConnector(c),
        (id) => this.deleteConnector(id)
      );
    }
    
    return null;
  }
  
  handleNodeClick(nodeId: string): ICommand | null {
    if (!this.isActive) return null;
    
    if (!this.firstNodeId) {
      // First click - select source node
      this.firstNodeId = nodeId;
      return null;
    } else {
      // Second click - create connector
      const fromId = this.firstNodeId;
      const toId = nodeId;
      
      // Don't allow self-connections
      if (fromId === toId) {
        this.firstNodeId = null;
        return null;
      }
      
      // Check if connector already exists
      if (this.connectorExists(fromId, toId)) {
        this.firstNodeId = null;
        return null;
      }
      
      const connector = this.createConnector(fromId, toId, this.connectionType);
      this.firstNodeId = null;
      
      return new CreateConnectorCommand(
        connector,
        (c) => this.addConnector(c),
        (id) => this.deleteConnector(id)
      );
    }
  }
  
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const connectionTypes = this.getConnectionTypes();
      const [selectedType, setSelectedType] = React.useState(connectionTypes[0]?.value || 'default');
      const [isActive, setIsActive] = React.useState(false);
      
      const handleSelectType = (type: string) => {
        setSelectedType(type);
        this.connectionType = type;
        setIsActive(true);
        this.isActive = true;
        // Trigger connection mode
        if (this.setConnectionState) {
          this.setConnectionState(true, null);
        }
        onExecute({ type });
      };
      
      // Map connection type icons to Lucide icons
      const getConnectionIcon = (type: string) => {
        switch (type) {
          case 'marriage': return Heart;
          case 'child': return Baby;
          case 'sibling': return Users;
          default: return Link;
        }
      };

      return ToolComponentFactory.createContainer([
        ToolComponentFactory.createSelect({
          icon: Link,
          placeholder: isActive ? 'Connecting...' : 'Marriage',
          value: selectedType,
          onValueChange: handleSelectType,
          options: connectionTypes.map(type => ({
            value: type.value,
            label: type.label,
            icon: getConnectionIcon(type.value),
            description: undefined
          }))
        }),
        
        this.firstNodeId && React.createElement('div', {
          key: 'status',
          className: 'px-2 py-1 bg-orange-50 rounded text-xs text-orange-700 border border-orange-200'
        }, 'Click second node to connect...')
      ].filter(Boolean));
    };
  }
}
